import type { Mappable } from '@atlaskit/editor-prosemirror/transform';
import { Step, StepResult } from '@atlaskit/editor-prosemirror/transform';
import type { Node as ProseMirrorNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { Slice, Fragment } from '@atlaskit/editor-prosemirror/model';

/**
 * For more context on what this is about:
 * @see https://discuss.prosemirror.net/t/preventing-image-placeholder-replacement-from-being-undone/1394
 */
export class SetAttrsStep extends Step {
	pos: number;
	attrs: object;

	constructor(pos: number, attrs: object) {
		super();
		this.pos = pos;
		this.attrs = attrs;
	}

	apply(doc: ProseMirrorNode): StepResult {
		const target = doc.nodeAt(this.pos);
		if (!target) {
			return StepResult.fail('No node at given position');
		}

		if (target.isText) {
			return StepResult.fail('Target is a text node. Attributes are not allowed.');
		}

		const attrs = {
			...(target.attrs || {}),
			...(this.attrs || {}),
		};

		const newNode = target.type.create(attrs, Fragment.empty, target.marks);
		const slice = new Slice(Fragment.from(newNode), 0, target.isLeaf ? 0 : 1);
		return StepResult.fromReplace(doc, this.pos, this.pos + 1, slice);
	}

	invert(doc: ProseMirrorNode): SetAttrsStep {
		const target = doc.nodeAt(this.pos);
		return new SetAttrsStep(this.pos, target ? target.attrs : {});
	}

	map(mapping: Mappable): SetAttrsStep | null {
		const result = mapping.mapResult(this.pos, 1);
		return result.deleted ? null : new SetAttrsStep(result.pos, this.attrs);
	}

	toJSON(): {
        attrs: object;
        pos: number;
        stepType: string;
    } {
		return { stepType: 'setAttrs', pos: this.pos, attrs: this.attrs };
	}

	static fromJSON(_schema: Schema, json: { attrs: object; pos?: number }): SetAttrsStep {
		if (typeof json.pos !== 'number' || (json.attrs !== null && typeof json.attrs !== 'object')) {
			throw new RangeError('Invalid input for SetAttrsStep.fromJSON');
		}
		return new SetAttrsStep(json.pos, json.attrs);
	}
}

Step.jsonID('setAttrs', SetAttrsStep);
